import React from 'react';
import Avatar from '../Avatar';
import Button from '../Button';
import CopyIcon from '../CustomIcons/CopyIcon';
import ThumbsDownIcon from '../CustomIcons/ThumbsDownIcon';
import ThumbsUpIcon from '../CustomIcons/ThumbsUpIcon';
import Typography from '../Typography';
import ChatThinking from './ChatThinking';
import { compactCount } from './tokenText';

export type ChatRole = 'user' | 'assistant';

/**
 * Three distinct treatments, not two plus a repaint of one: `plain` is the bare turn, `card`
 * frames a reply with its own status, copy/rating actions and a token line, and `bubble` is a
 * message-app chat style with no chrome beyond the bubble itself. All three appear in the
 * design-sync previews — keep them that way rather than folding `bubble` back into `card`.
 */
export type ChatMessageVariant = 'plain' | 'card' | 'bubble';

export type ChatMessageStatus = 'streaming' | 'completed' | 'error';

export type ChatFeedback = 'up' | 'down';

export interface ChatMessageUsage {
    /** The model as a reader would name it, e.g. "Claude Haiku 4.5". */
    model: string;
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
}

export interface ChatMessageProps {
    role: ChatRole;
    /**
     * `card` frames the turn with a header and a footer — for a surface where a reply carries
     * its own status and cost. `plain` is the bare turn. `bubble` wraps the turn in a coloured,
     * asymmetrically-rounded bubble instead — no header, no footer, just the shape.
     */
    variant?: ChatMessageVariant;
    /** Markdown. Rendered through `Typography.MarkdownBlock`, so a reply may carry formatting. */
    content?: string;
    /** Anything belonging with the message — tool calls, reasoning, a busy indicator. */
    children?: React.ReactNode;
    /** Names a user turn and its avatar; labels an assistant turn's header. */
    name?: string;
    avatarSrc?: string;
    /** Shown in a `card` header. Omitted means the turn reports no state. */
    status?: ChatMessageStatus;
    /**
     * The reply has been asked for but nothing has arrived. Renders the same placeholder
     * `Chat.Thinking` does, in the body's place, so the turn does not jump when text lands.
     */
    loading?: boolean;
    /**
     * Offers a copy control. The handler decides what to copy: a turn may hold tool calls and
     * reasoning this component never sees, so it cannot honestly claim to know the text.
     */
    onCopy?: () => void;
    /** Offers thumbs up and down. */
    onFeedback?: (feedback: ChatFeedback) => void;
    usage?: ChatMessageUsage;
    className?: string;
}

const STATUS_LABELS: Record<ChatMessageStatus, string> = {
    streaming: 'Streaming',
    completed: 'Completed',
    error: 'Failed',
};

/**
 * The flattened corner points at its author — bottom-right on a right-aligned user bubble,
 * bottom-left on a left-aligned assistant one — so who spoke is legible before a word is read.
 * Not decoration: swapping these would point every bubble at the wrong side of the transcript.
 */
const USER_BUBBLE_RADIUS = 'rounded-tl-[12px] rounded-tr-[12px] rounded-br-[4px] rounded-bl-[12px]';
const ASSISTANT_BUBBLE_RADIUS =
    'rounded-tl-[12px] rounded-tr-[12px] rounded-br-[12px] rounded-bl-[4px]';

const usageText = ({ model, inputTokens, outputTokens, totalTokens }: ChatMessageUsage): string => {
    const counts = [
        inputTokens === undefined ? undefined : `${compactCount(inputTokens)} in`,
        outputTokens === undefined ? undefined : `${compactCount(outputTokens)} out`,
        totalTokens === undefined ? undefined : `${compactCount(totalTokens)} total`,
    ].filter((part): part is string => part !== undefined);

    return [model, ...counts].join(' · ');
};

/**
 * `outline` rather than `ghost`: a bare glyph does not read as something to press, and ghost
 * only draws its border on hover, which a pointer finds and nothing else does.
 */
const MessageAction = ({
    label,
    icon,
    onClick,
}: {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
}) => (
    <Button
        variant="outline"
        size="xsmall"
        iconOnly
        icon={icon}
        tooltip={label}
        aria-label={label}
        onClick={onClick}
    />
);

/**
 * One turn in a transcript.
 *
 * A user turn sits to the right and hugs its own text, the way a sent message reads. An
 * assistant turn runs the full width in `plain`/`card`, so a long reply is not read through a
 * narrow column; in `bubble` both roles hug their own text instead, the way a messaging app reads.
 */
const ChatMessage: React.FC<ChatMessageProps> = ({
    role,
    variant = 'plain',
    content,
    children,
    name,
    avatarSrc,
    status,
    loading = false,
    onCopy,
    onFeedback,
    usage,
    className = '',
}) => {
    const body = (
        <div className="flex min-w-0 flex-col gap-2">
            {content ? <Typography.MarkdownBlock text={content} variant="flow" omitMargin /> : null}
            {children}
            {loading && !content ? <ChatThinking /> : null}
        </div>
    );

    if (role === 'user') {
        const boxClasses =
            variant === 'bubble'
                ? `${USER_BUBBLE_RADIUS} border-[var(--malachite-chat-user-bubble-border)] bg-[var(--malachite-chat-user-bubble-background)]`
                : 'rounded-lg border-[var(--malachite-accent-border)] bg-[var(--malachite-accent-background-glass)]';

        return (
            <div className={`flex min-w-0 items-start justify-end gap-2 ${className}`}>
                <div className={`min-w-0 max-w-[85%] border border-solid px-3 py-2 ${boxClasses}`}>
                    {body}
                </div>
                <div className="shrink-0 pt-[2px]">
                    <Avatar src={avatarSrc} name={name} size={24} shape="square" />
                </div>
            </div>
        );
    }

    const footer = onCopy || onFeedback || usage;

    if (variant === 'plain') {
        return <div className={`flex min-w-0 flex-col ${className}`}>{body}</div>;
    }

    if (variant === 'bubble') {
        // Hugs its content rather than filling the column: a flex row (not the plain/card
        // column) so the bubble's width tracks its text and only caps at the same 85% the
        // user turn already uses, instead of stretching to the full transcript width.
        return (
            <div className={`flex min-w-0 items-start ${className}`}>
                <div
                    className={`min-w-0 max-w-[85%] border border-solid px-3 py-2 text-[var(--malachite-chat-assistant-bubble-foreground)] ${ASSISTANT_BUBBLE_RADIUS} border-[var(--malachite-chat-assistant-bubble-border)] bg-[var(--malachite-chat-assistant-bubble-background)]`}
                >
                    {body}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`flex min-w-0 flex-col gap-3 rounded-[var(--malachite-radius-xxl,14px)] border border-solid border-[var(--malachite-card-border)] bg-[var(--malachite-card-background)] p-4 ${className}`}
        >
            {(name || status) && (
                <div className="flex items-center gap-2">
                    {name ? (
                        <span className="rounded-md bg-[var(--malachite-card-dimmed-background)] px-2 py-1 leading-none">
                            <Typography.Text size="small" fontWeight="semi-bold">
                                {name}
                            </Typography.Text>
                        </span>
                    ) : null}
                    {status ? (
                        <Typography.SecondaryText size="xsmall" textTransform="uppercase">
                            {STATUS_LABELS[status]}
                        </Typography.SecondaryText>
                    ) : null}
                </div>
            )}
            {body}
            {footer ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1">
                        {onCopy ? (
                            <MessageAction
                                label="Copy"
                                icon={<CopyIcon size={13} />}
                                onClick={onCopy}
                            />
                        ) : null}
                        {onFeedback ? (
                            <>
                                <MessageAction
                                    label="Good response"
                                    icon={<ThumbsUpIcon size={13} />}
                                    onClick={() => onFeedback('up')}
                                />
                                <MessageAction
                                    label="Bad response"
                                    icon={<ThumbsDownIcon size={13} />}
                                    onClick={() => onFeedback('down')}
                                />
                            </>
                        ) : null}
                    </div>
                    {usage ? (
                        <span className="flex min-w-0 items-center gap-1.5">
                            {/* Decorative, so it is hidden: the model is named in the text
                                beside it and a screen reader gains nothing from a dot. */}
                            <span
                                aria-hidden="true"
                                className="h-2 w-2 shrink-0 rounded-full bg-[var(--malachite-warning-background)]"
                            />
                            <Typography.SecondaryText size="xsmall">
                                {usageText(usage)}
                            </Typography.SecondaryText>
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default ChatMessage;
export { ChatMessage };
